import { useEffect } from "react";
import create from "zustand";

import { useSessionStore } from "../../containers/SessionManager/sessionStore";

import { buildWebSocketUrl } from "./utils";

export type ReviewCycleJobStatus = {
	name: ReviewCycleJobType;
	total: number;
	progress: number;
	finished: boolean;
	job_id: string;
};

export type ReviewCycleStatusMessage = {
	jobs: Record<ReviewCycleJobType, ReviewCycleJobStatus>;
	any_job_running: boolean;
	cycle_id: string;
	status?: "ready"; // Added when no jobs are running
};

export type ReviewCycleJobType =
	| "create-cycle" // Building/creating the review cycle
	| "add-subjects-to-cycle" // Adding subjects/users to cycle
	| "easy-adds-approve" // Bulk approving easy-add (360) forms
	| "review-cycle-update"; // Updating review cycle (close, delete, update-start-date)

export type JobStatus = {
	jobType: ReviewCycleJobType;
	isRunning: boolean;
	progress: number;
	total: number;
	progressPercentage: number;
	isComplete: boolean;
	noJobFound: boolean;
};

type ReviewCycleStatusState = {
	// WebSocket connection
	socket: WebSocket | null;
	isConnected: boolean;
	isConnecting: boolean;
	isDisconnecting: boolean;
	isInitialized: boolean;

	// Current data
	cycleId: string | null;
	currentStatus: ReviewCycleStatusMessage | null;

	// Job-specific data
	jobStatuses: Record<ReviewCycleJobType, ReviewCycleJobStatus | null>;

	// Computed values for all job types
	isBuilding: boolean; // create-cycle running
	isUpdating: boolean; // review-cycle-update running
	isAddingSubjects: boolean; // add-subjects-to-cycle running
	isApprovingEasyAdds: boolean; // easy-adds-approve running
	isAnyJobRunning: boolean; // any job running
	buildingProgressPercentage: number; // create-cycle progress
	updatingProgressPercentage: number; // review-cycle-update progress
	addingSubjectsProgressPercentage: number; // add-subjects progress
	approvingEasyAddsProgressPercentage: number; // easy-adds progress
	isDebug: boolean;

	// Actions
	connect: (cycleId: string) => void;
	disconnect: () => void;
	onMessage: (message: string) => void;
	updateComputedValues: () => void;
	reset: () => void;
	resetConnection: () => void;
	addLog: (level: "log" | "error" | "warn", text: string, ...args: any[]) => void;

	// Getters for specific job types
	getJobStatus: (jobType: ReviewCycleJobType) => ReviewCycleJobStatus | null;
};

const initialState = {
	socket: null,
	isConnected: false,
	isConnecting: false,
	isDisconnecting: false,
	isInitialized: false,
	cycleId: null,
	currentStatus: null,
	jobStatuses: {
		"create-cycle": null,
		"review-cycle-update": null,
		"add-subjects-to-cycle": null,
		"easy-adds-approve": null,
	},
	isBuilding: false,
	isUpdating: false,
	isAddingSubjects: false,
	isApprovingEasyAdds: false,
	isAnyJobRunning: false,
	buildingProgressPercentage: 0,
	updatingProgressPercentage: 0,
	addingSubjectsProgressPercentage: 0,
	approvingEasyAddsProgressPercentage: 0,
	isDebug: false,
};

export const useReviewCycleStatusStore = create<ReviewCycleStatusState>((set, get) => ({
	...initialState,

	// Private debug logging function
	addLog: (level: "log" | "error" | "warn", text: string, ...args: any[]) => {
		const { isDebug } = get();
		if (isDebug) {
			console[level](`[ReviewCycleStatus] ${text}`, ...args);
		}
	},

	connect: (cycleId: string, isDebug: boolean = false) => {
		const state = get();
		set({ isDebug });

		// Skip connecting if the user's session is already expired — the server
		// will just close the socket, which would otherwise cause a retry loop.
		if (useSessionStore.getState().isExpired) {
			get().addLog("log", "Skipping connect: session is expired");
			return;
		}

		// Don't reconnect if already connected or connecting to the same cycle
		if ((state.isConnected || state.isConnecting) && state.cycleId === cycleId) {
			get().addLog("log", `Already connected to cycle ${cycleId}`);
			return;
		}

		// Disconnect existing connection if connecting to different cycle
		if (state.socket && state.cycleId !== cycleId) {
			get().addLog("log", `Disconnecting from old cycle ${state.cycleId} to connect to ${cycleId}`);
			state.disconnect();
		}

		get().addLog("log", `🔄 Connecting to cycle ${cycleId}...`);
		const url = `/api/review_cycle/${cycleId}/jobs-status/`;
		const absoluteUrl = buildWebSocketUrl(url);
		const ws = new WebSocket(absoluteUrl);

		ws.onopen = () => {
			get().addLog("log", `✅ Connected to ${absoluteUrl}`);
			get().addLog("log", `Waiting for initial status message...`);
			set({
				isConnected: true,
				isConnecting: false,
				cycleId,
			});

			// Set up a timeout to detect if no message is received
			const messageTimeout = setTimeout(() => {
				const currentState = get();
				if (!currentState.isInitialized && currentState.isConnected && !currentState.isDebug) {
					get().addLog("error", "[ReviewCycleStatus] ⏰ No message received within 5 seconds of connection");
				}
			}, 5000);

			// Store timeout to clear it when message arrives
			(ws as any)._messageTimeout = messageTimeout;
		};

		ws.onmessage = (event) => {
			get().onMessage(event.data);
		};

		ws.onclose = (event) => {
			get().addLog("log", `🔌 Connection closed:`, event.code, event.reason);
			const state = get();
			get().addLog("log", `Was initialized: ${state.isInitialized}`);
			get().addLog("log", `Last status:`, state.currentStatus);

			// Clear any pending message timeout
			if ((ws as any)._messageTimeout) {
				clearTimeout((ws as any)._messageTimeout);
			}

			set({
				socket: null,
				isConnected: false,
				isDisconnecting: false,
				isInitialized: false,
				currentStatus: null,
				jobStatuses: {
					"create-cycle": null,
					"add-subjects-to-cycle": null,
					"easy-adds-approve": null,
					"review-cycle-update": null,
				},
			});
			get().updateComputedValues();
		};

		ws.onerror = (error) => {
			get().addLog("error", "[ReviewCycleStatus] WebSocket error:", error);
			set({
				isConnecting: false,
				isDisconnecting: false,
			});
		};

		set({
			socket: ws,
			isConnecting: true,
			cycleId,
			// Reset status when connecting to new cycle
			currentStatus: null,
			isInitialized: false,
			jobStatuses: {
				"create-cycle": null,
				"add-subjects-to-cycle": null,
				"easy-adds-approve": null,
				"review-cycle-update": null,
			},
		});
		get().updateComputedValues();
	},

	disconnect: () => {
		const { socket } = get();
		if (socket) {
			get().addLog("log", "Disconnecting WebSocket");
			set({ isDisconnecting: true });
			socket.close(1000, "Manual disconnect");
		}
	},

	onMessage: (messageData) => {
		try {
			get().addLog("log", "🔍 Raw message data:", messageData);
			const message: ReviewCycleStatusMessage = JSON.parse(messageData);
			get().addLog("log", "📨 Received jobs status message:", message);
			get().addLog("log", "📋 Message properties:", {
				cycleId: message.cycle_id,
				anyJobRunning: message.any_job_running,
				status: message.status,
				jobCount: Object.keys(message.jobs || {}).length,
			});

			// Clear the message timeout since we received a message
			const socket = get().socket;
			if (socket && (socket as any)._messageTimeout) {
				clearTimeout((socket as any)._messageTimeout);
				(socket as any)._messageTimeout = null;
			}

			// Handle error messages
			if ("error" in message) {
				console.error("[ReviewCycleStatus] Authorization error:", message);
				return;
			}

			// Update job statuses - clear all and then set active ones
			const newJobStatuses: Record<ReviewCycleJobType, ReviewCycleJobStatus | null> = {
				"create-cycle": null,
				"add-subjects-to-cycle": null,
				"easy-adds-approve": null,
				"review-cycle-update": null,
			};

			// Only set jobs that are currently active/reported
			Object.entries(message.jobs || {}).forEach(([jobType, jobStatus]) => {
				newJobStatuses[jobType as ReviewCycleJobType] = jobStatus;
			});

			set({
				currentStatus: message,
				jobStatuses: newJobStatuses,
				isInitialized: true,
			});

			get().addLog("log", "[ReviewCycleStatus] 🔄 Job statuses updated:", newJobStatuses);

			// Log ready status
			if (message.status === "ready") {
				get().addLog(
					"log",
					"[ReviewCycleStatus] ✅ Review cycle is ready - no jobs running (connection stays open)"
				);
			} else if (message.any_job_running) {
				get().addLog("log", "[ReviewCycleStatus] 🔄 Jobs still running:", Object.keys(message.jobs));
			}

			// Update computed values
			get().updateComputedValues();

			// Log computed values after update
			const state = get();

			// Log job completions
			Object.entries(message.jobs || {}).forEach(([jobType, jobStatus]) => {
				const wasRunning =
					state.jobStatuses[jobType as ReviewCycleJobType] &&
					!state.jobStatuses[jobType as ReviewCycleJobType]?.finished;
				if (wasRunning && jobStatus.finished) {
					get().addLog("log", `[ReviewCycleStatus] ${jobType} completed!`);
				}
			});
		} catch (error) {
			get().addLog("error", "[ReviewCycleStatus] Error parsing message:", error, messageData);
		}
	},

	updateComputedValues: () => {
		const state = get();
		const jobs = state.jobStatuses;

		// Individual job statuses
		const createCycleJob = jobs["create-cycle"];
		const addSubjectsJob = jobs["add-subjects-to-cycle"];
		const easyAddsJob = jobs["easy-adds-approve"];
		const updateJob = jobs["review-cycle-update"];

		const isBuilding = createCycleJob ? !createCycleJob.finished : false;
		const isAddingSubjects = addSubjectsJob ? !addSubjectsJob.finished : false;
		const isApprovingEasyAdds = easyAddsJob ? !easyAddsJob.finished : false;
		const isUpdating = updateJob ? !updateJob.finished : false;

		// Progress percentages
		const buildingProgressPercentage =
			createCycleJob && createCycleJob.total > 0
				? Math.round((createCycleJob.progress / createCycleJob.total) * 100)
				: 0;

		const addingSubjectsProgressPercentage =
			addSubjectsJob && addSubjectsJob.total > 0
				? Math.round((addSubjectsJob.progress / addSubjectsJob.total) * 100)
				: 0;

		const updatingProgressPercentage =
			updateJob && updateJob.total > 0 ? Math.round((updateJob.progress / updateJob.total) * 100) : 0;

		const approvingEasyAddsProgressPercentage =
			easyAddsJob && easyAddsJob.total > 0 ? Math.round((easyAddsJob.progress / easyAddsJob.total) * 100) : 0;

		// Any job running
		const isAnyJobRunning = isBuilding || isUpdating || isAddingSubjects || isApprovingEasyAdds;

		set({
			isBuilding,
			isUpdating,
			isAddingSubjects,
			isApprovingEasyAdds,
			isAnyJobRunning,
			buildingProgressPercentage,
			updatingProgressPercentage,
			addingSubjectsProgressPercentage,
			approvingEasyAddsProgressPercentage,
		});
	},

	getJobStatus: (jobType: ReviewCycleJobType) => {
		return get().jobStatuses[jobType];
	},

	reset: () => {
		const state = get();
		if (state.socket) {
			state.disconnect();
		}
		set(initialState);
	},

	resetConnection: () => {
		const state = get();
		get().addLog("log", "[ReviewCycleStatus] 🔄 Manual reset triggered");

		// Store current cycle ID before reset
		const currentCycleId = state.cycleId;

		// Disconnect current connection if exists
		if (state.socket) {
			get().addLog("log", "[ReviewCycleStatus] Disconnecting existing connection for reset");
			state.disconnect();
		}

		// Reset all state
		set({
			...initialState,
			socket: null,
		});

		// Reconnect if we had a cycle ID
		if (currentCycleId) {
			get().addLog("log", "[ReviewCycleStatus] Reconnecting to cycle:", currentCycleId);
			setTimeout(() => {
				get().connect(currentCycleId);
			}, 100); // Small delay to ensure cleanup
		}
	},
}));

/**
 * Hook to automatically manage review cycle status WebSocket connection
 * for all job types via the new unified endpoint
 *
 * @param cycleId - The review cycle ID to monitor
 * @param enabled - Whether to connect (default: true)
 * @returns Object with connection state and status information for all job types
 *
 * Example usage:
 * ```tsx
 * function ReviewCycleDetailsPage({ cycleId }) {
 *   const {
 *     isBuilding,
 *     isUpdating,
 *     isAddingSubjects,
 *     isApprovingEasyAdds,
 *     isAnyJobRunning,
 *     buildingProgressPercentage,
 *     updatingProgressPercentage,
 *     addingSubjectsProgressPercentage,
 *     approvingEasyAddsProgressPercentage,
 *     getJobStatus
 *   } = useReviewCycleStatus(cycleId);
 *
 *   if (isBuilding) {
 *     return <div>Building cycle: {buildingProgressPercentage}%</div>;
 *   }
 *
 *   if (isUpdating) {
 *     return <div>Updating cycle: {updatingProgressPercentage}%</div>;
 *   }
 *
 *   if (isAddingSubjects) {
 *     return <div>Adding subjects: {addingSubjectsProgressPercentage}%</div>;
 *   }
 *
 *   if (isApprovingEasyAdds) {
 *     return <div>Approving 360 forms: {approvingEasyAddsProgressPercentage}%</div>;
 *   }
 *
 *   return <div>Review cycle is ready!</div>;
 * }
 * ```
 */
export const useReviewCycleStatus = (cycleId: string | null, enabled: boolean = true) => {
	const store = useReviewCycleStatusStore();
	const isSessionExpired = useSessionStore((s) => s.isExpired);

	// Add global window function for testing/debugging
	useEffect(() => {
		if (typeof window !== "undefined") {
			(window as any).resetReviewCycleWebSocket = () => {
				store.resetConnection();
			};
			// Toggle verbose logging when localStorage key is set
			try {
				const shouldDebug = window.localStorage.getItem("py.reviewCycle.wsDebug") === "1";
				if (shouldDebug && !store.isDebug) {
					useReviewCycleStatusStore.setState({ isDebug: true });
				}
			} catch (error) {
				console.error("Error toggling verbose logging on the WS:", error);
			}
		}
	}, [store.resetConnection]);

	// While the session is expired, proactively drop the socket. After a laptop
	// sleep the browser may not have surfaced the server-side close yet, so the
	// store can still look "connected" with a dead socket — which would block the
	// reconnect below from firing once the session is extended.
	useEffect(() => {
		if (isSessionExpired && store.socket) {
			store.disconnect();
		}
	}, [isSessionExpired, store.socket, store.disconnect]);

	// Auto-connect when enabled. Short-circuits while the session is expired; when
	// the session is extended, isSessionExpired flips to false and this effect re-runs.
	useEffect(() => {
		if (!enabled || !cycleId || isSessionExpired) {
			return;
		}
		if (!store.isConnected || !store.socket || store.cycleId !== cycleId) {
			store.connect(cycleId);
		}
	}, [cycleId, enabled, isSessionExpired, store.isConnected, store.socket, store.cycleId, store.connect]);

	// Cleanup on unmount only
	useEffect(() => {
		return () => {
			if (enabled && cycleId) {
				store.disconnect();
			}
		};
	}, []);

	return {
		// Connection state
		isConnected: store.isConnected,
		isConnecting: store.isConnecting,
		isInitialized: store.isInitialized,

		// Individual job statuses
		isBuilding: store.isBuilding,
		isAddingSubjects: store.isAddingSubjects,
		isApprovingEasyAdds: store.isApprovingEasyAdds,
		isUpdating: store.isUpdating,

		// Progress percentages for each job type
		buildingProgressPercentage: store.buildingProgressPercentage,
		updatingProgressPercentage: store.updatingProgressPercentage,
		addingSubjectsProgressPercentage: store.addingSubjectsProgressPercentage,
		approvingEasyAddsProgressPercentage: store.approvingEasyAddsProgressPercentage,

		// Combined status
		isAnyJobRunning: store.isAnyJobRunning,

		// Job details
		currentStatus: store.currentStatus,
		getJobStatus: store.getJobStatus,

		// Actions
		connect: store.connect,
		disconnect: store.disconnect,
		reset: store.reset,
		resetConnection: store.resetConnection,
	};
};

// TODO: Remove this mock once real API integration is complete
/**
 * Mock function to simulate review cycle close operation for frontend development
 * This simulates the async job creation and progress tracking that happens when
 * closing a review cycle through the API
 */
export const mockCloseReviewCycle = (cycleId: string) => {
	// Simulate the async job creation that happens in the API
	const mockJobId = `mock-job-${cycleId}-${Date.now()}`;
	const mockJob: ReviewCycleJobStatus = {
		name: "review-cycle-update",
		progress: 0,
		total: 100,
		finished: false,
		job_id: mockJobId,
	};

	// Get current store state
	const store = useReviewCycleStatusStore.getState();

	// Set initial job status to simulate starting the close operation
	store.jobStatuses["review-cycle-update"] = mockJob;
	store.updateComputedValues();

	// Simulate progress over time
	let progress = 0;
	const progressInterval = setInterval(() => {
		progress += Math.random() * 15; // Random progress increments

		if (progress >= 100) {
			// Complete the job
			clearInterval(progressInterval);

			store.jobStatuses["review-cycle-update"] = {
				...mockJob,
				progress: 100,
				finished: true,
			};
		} else {
			// Update progress
			store.jobStatuses["review-cycle-update"] = {
				...mockJob,
				progress: Math.round(progress),
			};
		}

		store.updateComputedValues();
	}, 1000); // Update every second

	// Return cleanup function
	return () => {
		clearInterval(progressInterval);
	};
};

/**
 * Mock function to simulate review cycle delete operation for frontend development
 */
export const mockDeleteReviewCycle = (cycleId: string) => {
	const mockJob: ReviewCycleJobStatus = {
		name: "review-cycle-update",
		progress: 0,
		total: 50, // Delete operations are typically faster
		finished: false,
		job_id: `mock-delete-job-${cycleId}-${Date.now()}`,
	};

	const store = useReviewCycleStatusStore.getState();
	store.jobStatuses["review-cycle-update"] = mockJob;
	store.updateComputedValues();

	// Simulate faster delete progress
	let progress = 0;
	const progressInterval = setInterval(() => {
		progress += Math.random() * 25;

		if (progress >= 50) {
			clearInterval(progressInterval);

			store.jobStatuses["review-cycle-update"] = {
				...mockJob,
				progress: 50,
				finished: true,
			};
		} else {
			store.jobStatuses["review-cycle-update"] = {
				...mockJob,
				progress: Math.round(progress),
			};
		}

		store.updateComputedValues();
	}, 800);

	return () => {
		clearInterval(progressInterval);
	};
};

/**
 * Mock function to simulate review cycle start date update operation
 */
export const mockUpdateStartDate = (cycleId: string) => {
	const mockJob: ReviewCycleJobStatus = {
		name: "review-cycle-update",
		progress: 0,
		total: 30, // Start date updates are quick
		finished: false,
		job_id: `mock-startdate-job-${cycleId}-${Date.now()}`,
	};

	const store = useReviewCycleStatusStore.getState();
	store.jobStatuses["review-cycle-update"] = mockJob;
	store.updateComputedValues();

	// Simulate quick update progress
	let progress = 0;
	const progressInterval = setInterval(() => {
		progress += Math.random() * 15;

		if (progress >= 30) {
			clearInterval(progressInterval);

			store.jobStatuses["review-cycle-update"] = {
				...mockJob,
				progress: 30,
				finished: true,
			};
		} else {
			store.jobStatuses["review-cycle-update"] = {
				...mockJob,
				progress: Math.round(progress),
			};
		}

		store.updateComputedValues();
	}, 600);

	return () => {
		clearInterval(progressInterval);
	};
};

/**
 * Mock function to clear any running update operations
 * Useful for testing different states
 */
export const mockClearUpdateOperation = () => {
	const store = useReviewCycleStatusStore.getState();
	store.jobStatuses["review-cycle-update"] = null;
	store.updateComputedValues();
};
